// react context for setting locale
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react'
import { useWindowSize } from 'usehooks-ts'

type Locale = Record<string, string | ReactNode>

interface MFContextType {
  _t: (key: string, params?: Record<string, string | number>) => string
  currentLocale: Locale
  currentLanguage: 'de' | 'fr'
  colorMode: 'light' | 'dark'
  isMobile: boolean
  tenantKey: string
  windowHeight: number
  trackEvent:
    | ((
        event: string,
        eventData: {
          [key: string]: any
        },
      ) => void)
    | undefined
  properties: {
    [key: string]: string
  }
  customProps?: Record<string, any>
  placeHolderHeight?: number
}

const MFContext = createContext<MFContextType | undefined>(undefined)

interface MFContextProviderProps {
  children?: ReactNode
  locale: 'de' | 'fr'
  colorMode: 'light' | 'dark'
  translations?: {
    de: Locale
    fr?: Locale
  }
  tenantKey: string
  properties: {
    [key: string]: string
  }
  trackEvent:
    | ((
        event: string,
        eventData: {
          [key: string]: any
        },
      ) => void)
    | undefined
  customProps?: { [key: string]: any }
  placeHolderHeight?: number
}

export const MFContextProvider: React.FC<MFContextProviderProps> = ({
  children,
  locale,
  translations,
  colorMode,
  tenantKey,
  trackEvent,
  properties,
  customProps,
  placeHolderHeight = 300,
}) => {
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 712
  const [windowHeight, setHeight] = useState(0)

  useEffect(() => {
    setHeight(windowSize.height)
  }, [windowSize.height])
  /*

  Translations

  */
  const currentLocale =
    locale === 'fr' && translations?.fr
      ? translations.fr
      : (translations?.de ?? {})

  /**
   * Translates a key into the current locale's text, with optional parameter substitution
   * @param key - The translation key to look up in the currentLocale object
   * @param params - Optional object containing key-value pairs for parameter substitution.
   *                Parameters in the translation string should be formatted as {{paramName}}
   * @returns The translated string/ReactNode with parameters replaced, or "[key]" if translation is missing
   * @example
   * // With string translation "greeting": "Hello {{name}}!"
   * _t("greeting", { name: "John" }) // returns "Hello John!"
   *
   * // With ReactNode translation
   * _t("component", { name: "John" }) // returns the ReactNode as-is
   *
   * // With missing translation
   * _t("missing_key") // returns "[missing_key]"
   */
  const _t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      let translation =
        key in currentLocale ? currentLocale[key] : '[' + key + ']'
      if (!(key in currentLocale)) {
        console.log(
          '%cTranslation missing: ' + key + ' in ' + locale,
          'color: red',
        )
      }
      if (params && typeof translation === 'string') {
        Object.keys(params).forEach((paramKey) => {
          translation = (translation as string).replace(
            new RegExp('{{' + paramKey + '}}', 'g'),
            params[paramKey] as string,
          )
        })
      } else if (params && typeof translation !== 'string') {
        console.warn(
          `Parameter substitution not supported for ReactNode translation: ${key}`,
        )
      }

      // Convert ReactNode to string if needed
      return typeof translation === 'string' ? translation : `[${key}]`
    },
    [currentLocale, locale],
  )

  // Memoize trackEvent if it exists
  const memoizedTrackEvent = useCallback(trackEvent || (() => {}), [trackEvent])

  const value = useMemo(() => {
    return {
      _t,
      trackEvent: memoizedTrackEvent,
      currentLocale,
      currentLanguage: locale,
      colorMode,
      isMobile,
      tenantKey,
      windowHeight,
      properties,
      placeHolderHeight,
      customProps,
    }
  }, [
    _t,
    memoizedTrackEvent,
    currentLocale,
    locale,
    colorMode,
    isMobile,
    tenantKey,
    windowHeight,
    properties,
    placeHolderHeight,
    customProps,
  ])

  return <MFContext.Provider value={value}>{children}</MFContext.Provider>
}

export const useMFContext = () => {
  const context = useContext(MFContext)
  if (context === undefined) {
    throw new Error('useMFContext must be used within a MFContextProvider')
  }
  return context
}

const STATIC_BASE_URL =
  'https://interaktiv-mf.tagesanzeiger.ch/2024/srg-halbierungsintiative'

export function url(path_in_public_folder: string) {
  const path = path_in_public_folder.startsWith('/')
    ? path_in_public_folder
    : '/' + path_in_public_folder
  return STATIC_BASE_URL + path
}
